// SCR-007's customer-invoice and statement panels (REQ-038, REQ-039). Every money figure is
// formatted by `ui/money.ts` from the integer µUSD the API returns; this module derives no price,
// margin or total of its own. Negative-margin usage lines are marked with a text label (never
// colour alone); a `not_billed` org-month or a still-open CUR finalization each get their own
// named state rather than an empty table.
import type { ApiClient } from "@nexus/ui";
import type { ApiError } from "@nexus/ui";
import type { components } from "../../api/schema";
import { markupBpFrom, money, ratioBasisPoints, type MoneyDisplay } from "@nexus/ui";

export type CustomerInvoice = components["schemas"]["CustomerInvoice"];
export type CustomerInvoiceLine = components["schemas"]["CustomerInvoiceLine"];
export type CustomerInvoiceLineKind = components["schemas"]["CustomerInvoiceLineKind"];
export type PricingRule = components["schemas"]["PricingRule"];
export type MarkupBasis = components["schemas"]["MarkupBasis"];
export type ExpenseBasisKind = components["schemas"]["ExpenseBasisKind"];
export type Statement = components["schemas"]["Statement"];
export type StatementLine = components["schemas"]["StatementLine"];

const KIND_LABELS: Record<CustomerInvoiceLineKind, string> = {
  usage: "Usage",
  late_after_close: "Late (after close)",
  corrected_tokens: "Corrected tokens",
  volume_discount: "Volume discount",
  trial_credit: "Trial credit",
  credit: "Credit",
  write_off: "Write-off",
  minimum_true_up: "Minimum true-up",
  platform_fee: "Platform fee",
  credit_carried_in: "Credit carried in",
};

export function lineKindLabel(kind: CustomerInvoiceLineKind): string {
  return KIND_LABELS[kind];
}

// ------------------------------------------------------------------------------- invoice states

/** SCR-007's named states for the customer-invoice panel; a `not_billed` org-month or a still-open
 *  CUR finalization are never rendered as an empty table. */
export type InvoiceState =
  | { kind: "loading" }
  | { kind: "not-billed" }
  | { kind: "cur-not-final" }
  | { kind: "absent" }
  | { kind: "error"; error: ApiError }
  | { kind: "loaded"; invoice: CustomerInvoice };

export function invoiceStateFromError(error: ApiError): InvoiceState {
  switch (error.code) {
    case "org_not_billed":
      return { kind: "not-billed" };
    case "cur_not_final":
      return { kind: "cur-not-final" };
    case "customer_invoice_absent":
      return { kind: "absent" };
    default:
      return { kind: "error", error };
  }
}

/** API-030: the JSON the panel loads, and the CSV and PDF the download links point at. */
export const CUSTOMER_INVOICE_PATH = "/v1/admin/orgs/{orgId}/billing-periods/{period}/customer-invoice";

export async function loadCustomerInvoice(
  client: ApiClient,
  orgId: string,
  period: string,
  basis?: ExpenseBasisKind,
): Promise<InvoiceState> {
  try {
    const invoice = await client.get(CUSTOMER_INVOICE_PATH, {
      path: { orgId, period },
      ...(basis ? { query: { basis } } : {}),
    });
    return { kind: "loaded", invoice };
  } catch (error) {
    return invoiceStateFromError(error as ApiError);
  }
}

export function finalBadgeLabel(final: boolean): string {
  return final ? "Final" : "Preview";
}

export function markupBasisNote(basis: MarkupBasis): string | null {
  return basis === "actual" ? "This plan prices from the org's finalized AWS bill (CUR), not the list price book." : null;
}

// ---------------------------------------------------------------------------------- invoice lines

export interface InvoiceLineRow {
  lineNo: number;
  kindLabel: string;
  model: string;
  tokenClass: string;
  tokens: string;
  costDisplay: MoneyDisplay | null;
  priceDisplay: MoneyDisplay | null;
  marginDisplay: MoneyDisplay | null;
  negativeMargin: boolean;
  unpriced: boolean;
  ruleLabel: PricingRule;
  /** The rule in words ("Markup", "Fixed rate", …). */
  ruleText: string;
  /** `basis=cur` only: the actual AWS expense allocated to the line. */
  expenseDisplay: MoneyDisplay | null;
}

const RULE_TEXT: Record<PricingRule, string> = {
  markup: "Markup",
  fixed: "Fixed rate",
  trial: "Trial",
  plan: "Plan",
  adjustment: "Adjustment",
};

export function pricingRuleText(rule: PricingRule): string {
  return RULE_TEXT[rule];
}

export function invoiceLineRow(line: CustomerInvoiceLine): InvoiceLineRow {
  const negativeMargin = line.marginMicroUsd !== null && line.marginMicroUsd !== undefined && line.marginMicroUsd < 0;
  return {
    lineNo: line.lineNo,
    kindLabel: lineKindLabel(line.kind),
    model: line.bedrockModelId ?? "—",
    tokenClass: line.tokenClass ?? "—",
    tokens: line.tokens === null || line.tokens === undefined ? "—" : line.tokens.toLocaleString(),
    costDisplay: line.listCostMicroUsd === null || line.listCostMicroUsd === undefined ? null : money(line.listCostMicroUsd),
    priceDisplay: line.priceMicroUsd === null || line.priceMicroUsd === undefined ? null : money(line.priceMicroUsd),
    marginDisplay: line.marginMicroUsd === null || line.marginMicroUsd === undefined ? null : money(line.marginMicroUsd),
    negativeMargin,
    unpriced: line.priceMicroUsd === null || line.priceMicroUsd === undefined,
    ruleLabel: line.pricingRule,
    ruleText: pricingRuleText(line.pricingRule),
    expenseDisplay: line.expenseMicroUsd === null || line.expenseMicroUsd === undefined ? null : money(line.expenseMicroUsd),
  };
}

export interface InvoiceTotalsView {
  finalLabel: string;
  markupBasisNote: string | null;
  priceDisplay: MoneyDisplay;
  listCostDisplay: MoneyDisplay;
  marginDisplay: MoneyDisplay;
  creditCarriedOutDisplay: MoneyDisplay | null;
  provisional: boolean;
  /** Profit over AWS list cost, and the share of the bill kept (both basis points; null at zero). */
  markupBp: bigint | null;
  marginBp: bigint | null;
  /** `basis=cur`: the actual AWS bill and profit over it; null otherwise or before the CUR is in. */
  expenseDisplay: MoneyDisplay | null;
  actualMarginDisplay: MoneyDisplay | null;
}

export function invoiceTotals(invoice: CustomerInvoice): InvoiceTotalsView {
  return {
    finalLabel: finalBadgeLabel(invoice.final),
    markupBasisNote: markupBasisNote(invoice.markupBasis),
    priceDisplay: money(invoice.priceMicroUsd),
    listCostDisplay: money(invoice.listCostMicroUsd),
    marginDisplay: money(invoice.marginMicroUsd),
    creditCarriedOutDisplay: invoice.creditCarriedOutMicroUsd > 0 ? money(invoice.creditCarriedOutMicroUsd) : null,
    provisional: invoice.provisional,
    markupBp: markupBpFrom(invoice.marginMicroUsd, invoice.listCostMicroUsd),
    marginBp: ratioBasisPoints(invoice.marginMicroUsd, invoice.priceMicroUsd),
    expenseDisplay: invoice.expenseMicroUsd === null ? null : money(invoice.expenseMicroUsd),
    actualMarginDisplay: invoice.actualMarginMicroUsd === null ? null : money(invoice.actualMarginMicroUsd),
  };
}

export const EXPENSE_BASIS_OPTIONS: readonly ExpenseBasisKind[] = ["list", "cur"];

/** The cost-basis switch in plain words (the redesign: Basis → Cost basis). */
export const EXPENSE_BASIS_LABELS: Readonly<Record<ExpenseBasisKind, string>> = { list: "AWS list price", cur: "Actual AWS bill" };

// -------------------------------------------------------------------------------- CSV / basis

export function nextExpenseBasis(current: ExpenseBasisKind): ExpenseBasisKind {
  return current === "list" ? "cur" : "list";
}

// ------------------------------------------------------------------------------------ statement

export type StatementState =
  | { kind: "loading" }
  | { kind: "not-billed" }
  | { kind: "error"; error: ApiError }
  | { kind: "loaded"; statement: Statement };

export function statementStateFromError(error: ApiError): StatementState {
  return error.code === "org_not_billed" ? { kind: "not-billed" } : { kind: "error", error };
}

export async function loadStatement(client: ApiClient, orgId: string, period: string): Promise<StatementState> {
  try {
    const statement = await client.get("/v1/orgs/{orgId}/billing-periods/{period}/statement", { path: { orgId, period } });
    return { kind: "loaded", statement };
  } catch (error) {
    return statementStateFromError(error as ApiError);
  }
}

export interface StatementLineRow {
  lineNo: number;
  description: string;
  priceDisplay: MoneyDisplay | null;
}

export function statementLineRow(line: StatementLine): StatementLineRow {
  return {
    lineNo: line.lineNo,
    description: line.description,
    priceDisplay: line.priceMicroUsd === null || line.priceMicroUsd === undefined ? null : money(line.priceMicroUsd),
  };
}
